import React from 'react';
import { makeStyles, shorthands, tokens } from '@fluentui/react-components';

interface ProgressTrackerProps {
  currentStep: number;
  steps: string[];
  title: string;
  description?: string;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    ...shorthands.padding('24px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    textAlign: 'center',
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    marginBottom: '8px',
  },
  description: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  progressBar: {
    display: 'flex',
    gap: '2px',
    marginTop: '8px',
  },
  segment: {
    flex: 1,
    height: '8px',
    backgroundColor: tokens.colorNeutralStroke2,
    position: 'relative',
    transition: 'background-color 0.3s ease',
  },
  segmentCompleted: {
    backgroundColor: '#107C10', // Green for completed
  },
  segmentCurrent: {
    backgroundColor: '#107C10', // Green for current
  },
  labelsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  label: {
    flex: 1,
    textAlign: 'center',
    fontSize: '13px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground2,
  },
  labelActive: {
    color: '#107C10',
    fontWeight: 600,
  },
});

const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  currentStep,
  steps,
  title,
  description
}) => {
  const styles = useStyles();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{title}</div>
        {description && <div className={styles.description}>{description}</div>}
      </div>

      {/* Labels */}
      <div className={styles.labelsContainer}>
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isCurrent = stepNumber === currentStep;

          return (
            <div
              key={index}
              className={`${styles.label} ${isCurrent ? styles.labelActive : ''}`}
            >
              {step}
            </div>
          );
        })}
      </div>

      {/* Progress Bar Segments */}
      <div className={styles.progressBar}>
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isCompleted = stepNumber < currentStep;
          const isCurrent = stepNumber === currentStep;

          return (
            <div
              key={index}
              className={`${styles.segment} ${
                isCompleted
                  ? styles.segmentCompleted
                  : isCurrent
                  ? styles.segmentCurrent
                  : ''
              }`}
            />
          );
        })}
      </div>
    </div>
  );
};

export default ProgressTracker;
